import { useEffect, useState, type FormEvent } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useProjectDetail, useUpdateProject, useArchiveProject } from '../api/queries/projects';
import { useClients } from '../api/queries/clients';
import type { ProjectStatus } from '@stfors/shared-types';
import { useTimeEntries } from '../api/queries/time-entries';
import { useEvidenceList } from '../api/queries/evidence';
import { useExpenses } from '../api/queries/expenses';
import { useUnbilledWipReport } from '../api/queries/reports';
import { useInvoices } from '../api/queries/invoices';
import {
  useProjectNotes,
  useCreateProjectNote,
  useDeleteProjectNote,
} from '../api/queries/project-notes';
import {
  useProjectTasks,
  useAssignableUsers,
  useCreateProjectTask,
  useUpdateProjectTask,
  useDeleteProjectTask,
} from '../api/queries/project-tasks';
import {
  useTaskTemplates,
  useApplyTaskTemplate,
} from '../api/queries/task-templates';
import type { ProjectTaskStatus } from '@stfors/shared-types';
import { ApiError } from '../api/client';
import { OrgPicker } from '../components/OrgPicker';
import { PersonPicker } from '../components/PersonPicker';
import { BillToBlock } from '../components/BillToBlock';
import { AddClientModal, ADD_CLIENT_VALUE } from '../components/AddClientModal';
import { can } from '../auth/store';
import { fmtDate, fmtDateMaybeTime } from '../utils/date';
import { DateInput } from '../components/DateInput';
import { ProjectDocuments } from '../components/ProjectDocuments';
import { ProjectMembersSection } from '../components/ProjectMembersSection';
import { ProjectMattersSection } from '../components/ProjectMattersSection';
import { LookupSelect } from '../components/LookupSelect';
import { IconAction } from '../components/IconAction';
import { useLookupLabel } from '../api/queries/lookups';
import './ProjectDetailPage.css';

// Project status / case type / billing type / evidence status labels now come
// from Settings → Lookups via useLookupLabel(...). Invoice status stays a local
// map (no lookup category for it).
const INVOICE_STATUS_LABEL: Record<string, string> = {
  draft: 'Draft',
  sent: 'Sent',
  paid: 'Paid',
  overdue: 'Overdue',
  void: 'Void',
};

import { formatMoney } from '../utils/money';
import { Toggle } from '../components/Toggle';

function fmtHours(hoursStr: string): string {
  const n = Number(hoursStr);
  return Number.isFinite(n) ? n.toFixed(2) : hoursStr;
}

export function ProjectDetailPage(): JSX.Element {
  const { id: idRaw } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const idNum = idRaw ? Number(idRaw) : NaN;
  const id = Number.isInteger(idNum) && idNum > 0 ? idNum : null;

  const projectQuery = useProjectDetail(id);
  const clientsQuery = useClients();
  const updateProject = useUpdateProject(id ?? 0);
  const archiveProject = useArchiveProject(id ?? 0);
  const timeEntriesQuery = useTimeEntries(id ?? undefined);
  const evidenceQuery = useEvidenceList(id ?? undefined);
  const expensesQuery = useExpenses(id ?? undefined);
  const invoicesQuery = useInvoices(id !== null ? { project_id: id } : {});
  // Dollar value of this project's unbilled work, priced off its effective
  // rate sheet — the unbilled COUNT below says how many entries are
  // outstanding, not what they are worth. Must stay above the early
  // returns further down or the hook order changes between renders.
  const wipQuery = useUnbilledWipReport(id);
  const notesQuery = useProjectNotes(id);
  const createNote = useCreateProjectNote();
  const deleteNote = useDeleteProjectNote(id ?? 0);
  const tasksQuery = useProjectTasks(id);
  const assignableQuery = useAssignableUsers();
  const createTask = useCreateProjectTask();
  const updateTask = useUpdateProjectTask(id ?? 0);
  const deleteTask = useDeleteProjectTask(id ?? 0);
  const templatesQuery = useTaskTemplates();
  const applyTemplate = useApplyTaskTemplate();

  // Read-only labelers for admin-managed dropdown values (Settings → Lookups).
  const statusLabel = useLookupLabel('project_status');
  const caseTypeLabel = useLookupLabel('case_type');
  const billingLabel = useLookupLabel('billing_type');
  const evidenceStatusLabel = useLookupLabel('evidence_status');

  const [noteText, setNoteText] = useState('');
  const [noteImportant, setNoteImportant] = useState(false);
  const [noteError, setNoteError] = useState<string | null>(null);

  const [statusError, setStatusError] = useState<string | null>(null);
  // The representative saves on pick, and the override on blur — both re-render
  // the composed block, which is the point of showing it here.
  const [repError, setRepError] = useState<string | null>(null);
  const [override, setOverride] = useState('');
  // Seeded from the STORED value, so typing is never clobbered mid-edit: it
  // re-syncs on load and after a save, when the two already agree.
  const storedOverride = projectQuery.data?.project.bill_to_override ?? '';
  useEffect(() => { setOverride(storedOverride); }, [storedOverride]);
  const saveRep = async (patch: {
    rep_organization_id?: number | null;
    rep_person_id?: number | null;
    bill_to_override?: string | null;
    lead_attorney?: string | null;
  }) => {
    setRepError(null);
    try {
      await updateProject.mutateAsync(patch);
    } catch (err) {
      setRepError(err instanceof ApiError && err.status === 403
        ? "You don't have permission to change this project."
        : 'Could not save. Try again.');
    }
  };

  const [editing, setEditing] = useState(false);
  const [editForm, setEditForm] = useState<{
    client_id: number;
    title: string;
    description: string;
    jurisdiction: string;
    lead_attorney: string;
    rep_person_id: number | null;
    court_date: number | null;
    target_end_date: number | null;
    priority: number;
  }>({
    client_id: 0, title: '', description: '', jurisdiction: '', lead_attorney: '',
    rep_person_id: null, court_date: null, target_end_date: null, priority: 3,
  });
  const [editError, setEditError] = useState<string | null>(null);
  const [addClientOpen, setAddClientOpen] = useState(false);
  // A client created from the edit form isn't in the cached list yet, so the
  // <option> that keeps the select showing the right name has to come from here.
  const [newClient, setNewClient] = useState<{ id: number; name: string } | null>(null);

  const [taskText, setTaskText] = useState('');
  const [taskAssignee, setTaskAssignee] = useState<number | ''>('');
  const [taskDueDate, setTaskDueDate] = useState<number | null>(null);
  const [taskError, setTaskError] = useState<string | null>(null);
  const [showApplyPicker, setShowApplyPicker] = useState(false);
  const [applyTemplateId, setApplyTemplateId] = useState<number | ''>('');
  const [applyError, setApplyError] = useState<string | null>(null);

  const startEdit = () => {
    if (!projectQuery.data) return;
    const p = projectQuery.data.project;
    setEditForm({
      client_id: p.client_id,
      title: p.title,
      description: p.description ?? '',
      jurisdiction: p.jurisdiction ?? '',
      lead_attorney: p.lead_attorney ?? '',
      rep_person_id: p.rep_person_id ?? null,
      court_date: p.court_date ?? null,
      target_end_date: p.target_end_date ?? null,
      priority: p.priority,
    });
    setEditError(null);
    setEditing(true);
  };

  const onSaveEdit = async (e: FormEvent) => {
    e.preventDefault();
    setEditError(null);
    if (editForm.title.trim().length === 0) {
      setEditError('Title is required.');
      return;
    }
    try {
      await updateProject.mutateAsync({
        // Only when it CHANGED: sending the same id would still hit the
        // has-invoices guard and block an otherwise ordinary edit.
        ...(editForm.client_id !== p.client_id ? { client_id: editForm.client_id } : {}),
        title: editForm.title.trim(),
        description: editForm.description.trim() || undefined,
        jurisdiction: editForm.jurisdiction.trim() || undefined,
        lead_attorney: editForm.lead_attorney.trim() || undefined,
        // Only when it changed, so an ordinary edit never rewrites the rep —
        // and never turns an INHERITED rep (NULL) into an explicit one.
        ...(editForm.rep_person_id !== (p.rep_person_id ?? null)
          ? { rep_person_id: editForm.rep_person_id }
          : {}),
        court_date: editForm.court_date,
        target_end_date: editForm.target_end_date,
        priority: editForm.priority as 1 | 2 | 3 | 4 | 5,
      });
      setEditing(false);
    } catch (err) {
      if (err instanceof ApiError && err.status === 403) {
        setEditError("You don't have permission to edit this project.");
        return;
      }
      // 409 is the has-invoices guard on a client change — its message names
      // the count and what to do, so surface it rather than "try again".
      if (err instanceof ApiError && err.status === 409) {
        setEditError(err.message);
        return;
      }
      if (err instanceof ApiError && err.status === 400) {
        setEditError(err.message || 'Please check the highlighted fields.');
        return;
      }
      setEditError('Could not save. Try again.');
    }
  };

  const onChangeStatus = async (newStatus: ProjectStatus) => {
    setStatusError(null);
    try {
      await updateProject.mutateAsync({ status: newStatus });
    } catch (err) {
      if (err instanceof ApiError && err.status === 403) {
        setStatusError("You don't have permission to update this project.");
        return;
      }
      setStatusError('Could not update status. Try again.');
    }
  };

  const onAddTask = async (e: FormEvent) => {
    e.preventDefault();
    if (id === null) return;
    setTaskError(null);
    const trimmed = taskText.trim();
    if (trimmed.length === 0) return;

    try {
      await createTask.mutateAsync({
        project_id: id,
        text: trimmed,
        assignee_user_id: taskAssignee === '' ? undefined : taskAssignee,
        due_date: taskDueDate ?? undefined,
      });
      setTaskText('');
      setTaskAssignee('');
      setTaskDueDate(null);
    } catch (err) {
      if (err instanceof ApiError && err.status === 403) {
        setTaskError("You don't have permission to add tasks to this project.");
        return;
      }
      setTaskError('Could not save task. Try again.');
    }
  };

  const onApplyTemplate = async () => {
    if (id === null || applyTemplateId === '') return;
    setApplyError(null);
    try {
      await applyTemplate.mutateAsync({
        template_id: applyTemplateId,
        project_id: id,
      });
      setShowApplyPicker(false);
      setApplyTemplateId('');
    } catch (err) {
      if (err instanceof ApiError && err.status === 400) {
        setApplyError('Could not apply — template may be empty.');
        return;
      }
      if (err instanceof ApiError && err.status === 403) {
        setApplyError("You don't have permission to apply templates.");
        return;
      }
      setApplyError('Could not apply template. Try again.');
    }
  };

  const onCycleTaskStatus = (taskId: number, current: ProjectTaskStatus) => {
    const next: ProjectTaskStatus =
      current === 'open' ? 'in_progress'
      : current === 'in_progress' ? 'done'
      : current === 'done' ? 'open'
      : 'open';
    updateTask.mutate({ id: taskId, status: next });
  };

  const onAddNote = async (e: FormEvent) => {
    e.preventDefault();
    if (id === null) return;
    setNoteError(null);
    const trimmed = noteText.trim();
    if (trimmed.length === 0) return;
    try {
      await createNote.mutateAsync({
        project_id: id,
        content: trimmed,
        is_important: noteImportant || undefined,
      });
      setNoteText('');
      setNoteImportant(false);
    } catch (err) {
      if (err instanceof ApiError && err.status === 403) {
        setNoteError("You don't have permission to add notes to this project.");
        return;
      }
      setNoteError('Could not save note. Try again.');
    }
  };

  if (id === null) return <p className="pd-status pd-error">Invalid project id.</p>;
  if (projectQuery.isPending) return <p className="pd-status">Loading…</p>;
  if (projectQuery.error) {
    const err = projectQuery.error;
    if (err instanceof ApiError && err.status === 404) {
      return <p className="pd-status pd-error">Project not found.</p>;
    }
    if (err instanceof ApiError && err.status === 403) {
      return <p className="pd-status pd-error">You don't have permission to view this project.</p>;
    }
    return <p className="pd-status pd-error">Failed to load.</p>;
  }

  const p = projectQuery.data!.project;
  // Sorted by name so the picker reads like the Clients list. Includes the
  // project's current client even if it is inactive — dropping it would blank
  // the control on an edit that isn't about the client at all.
  const clients = [...(clientsQuery.data?.clients ?? [])].sort((a, b) => a.name.localeCompare(b.name));
  const timeEntries = timeEntriesQuery.data?.time_entries ?? [];
  const evidence = evidenceQuery.data?.evidence ?? [];
  const expenses = expensesQuery.data?.expenses ?? [];
  const invoices = invoicesQuery.data?.invoices ?? [];
  const notes = notesQuery.data?.project_notes ?? [];
  const tasks = tasksQuery.data?.project_tasks ?? [];
  const assignableUsers = assignableQuery.data?.users ?? [];

  const tasksOpen = tasks.filter((t) => t.status === 'open' || t.status === 'in_progress').length;
  const tasksDone = tasks.filter((t) => t.status === 'done').length;

  const expensesTotal = expenses.reduce((sum, ex) => sum + (Number(ex.amount) || 0), 0);
  // `!ex.billed` matters: without it an expense keeps counting as reimbursable
  // after it has been invoiced, so the figure only ever grows.
  const expensesReimbursable = expenses
    .filter((ex) => ex.reimbursable && !ex.billed)
    .reduce((sum, ex) => sum + (Number(ex.amount) || 0), 0);

  const totalHours = timeEntries
    .reduce((sum, te) => sum + (Number(te.hours) || 0), 0)
    .toFixed(2);
  const unbilledCount = timeEntries.filter((te) => te.invoice_id === null).length;
  const wipTotals = wipQuery.data?.totals;

  return (
    <div className="pd-page">
      <header className="pd-header">
        <button className="btn-text" onClick={() => navigate('/projects')}>
          ← All projects
        </button>
        <h2>
          <code className="pd-case">{p.case_number}</code>
          <span className="pd-title">{p.title}</span>
        </h2>
        {can('project.write') ? (
          <LookupSelect
            category="project_status"
            className={`pd-status-select status-${p.status}`}
            value={p.status}
            onChange={onChangeStatus}
            disabled={updateProject.isPending}
            aria-label="Project status"
          />
        ) : (
          <span className={`status-pill status-${p.status}`}>
            {statusLabel(p.status)}
          </span>
        )}
        {can('project.write') && !editing && (
          <IconAction action="edit" onClick={startEdit} className="pd-edit-btn" />
        )}
        {can('project.archive') && !editing && p.status !== 'archived' && (
          <button
            type="button"
            className="btn-charcoal-secondary pd-edit-btn"
            disabled={archiveProject.isPending}
            onClick={() => {
              if (!confirm('Archive this project? It will be hidden from active lists.')) return;
              setStatusError(null);
              archiveProject.mutate(undefined, {
                onError: (err) => {
                  if (err instanceof ApiError && err.status === 403) {
                    setStatusError("You don't have permission to archive this project.");
                  } else {
                    setStatusError('Could not archive. Try again.');
                  }
                },
              });
            }}
          >
            Archive
          </button>
        )}
      </header>

      {statusError && (
        <p className="pd-note-error" role="alert">{statusError}</p>
      )}

      {editing && (
        <form className="pd-edit-form" onSubmit={onSaveEdit}>
          <label className="pd-edit-field">
            <span>Client *</span>
            <select
              className="form-input"
              value={editForm.client_id}
              onChange={(e) => {
                if (e.target.value === ADD_CLIENT_VALUE) { setAddClientOpen(true); return; }
                setEditForm({ ...editForm, client_id: Number(e.target.value) });
              }}
              disabled={updateProject.isPending || clientsQuery.isPending}
            >
              {clients.map((c) => (
                <option key={c.id} value={c.id}>{c.name}</option>
              ))}
              {newClient && !clients.some((c) => c.id === newClient.id) && (
                <option value={newClient.id}>{newClient.name}</option>
              )}
              {can('client.write') && (
                <option value={ADD_CLIENT_VALUE}>Other — add a new client…</option>
              )}
            </select>
            {editForm.client_id !== p.client_id && (
              <span className="pd-rep-hint">
                Moving this matter to another client. Only possible while it has no
                invoices — its rate sheet and retainer follow the new client.
              </span>
            )}
          </label>
          <label className="pd-edit-field">
            <span>Title *</span>
            <input
              type="text"
              className="form-input"
              value={editForm.title}
              onChange={(e) => setEditForm({ ...editForm, title: e.target.value })}
              required
              disabled={updateProject.isPending}
            />
          </label>
          <label className="pd-edit-field">
            <span>Description</span>
            <textarea
              className="form-input pd-edit-textarea"
              value={editForm.description}
              onChange={(e) => setEditForm({ ...editForm, description: e.target.value })}
              rows={3}
              disabled={updateProject.isPending}
            />
          </label>
          <div className="pd-edit-row">
            <label className="pd-edit-field">
              <span>Jurisdiction</span>
              <input
                type="text"
                className="form-input"
                value={editForm.jurisdiction}
                onChange={(e) => setEditForm({ ...editForm, jurisdiction: e.target.value })}
                disabled={updateProject.isPending}
              />
            </label>
            <div className="pd-edit-field">
              <span>Lead attorney</span>
              <PersonPicker
                value={editForm.rep_person_id}
                // Projects predating the FK hold only a name; show it rather
                // than an empty box that looks like the value was lost.
                fallbackLabel={editForm.lead_attorney}
                onChange={(personId, person) => setEditForm((f) => ({
                  ...f,
                  lead_attorney: person?.displayName ?? '',
                  // Move the FK only for a matter that already had its OWN rep
                  // person — those two were in step and must stay in step. One
                  // INHERITING the client's rep keeps inheriting; naming a lead
                  // attorney here shouldn't quietly sever that.
                  rep_person_id: f.rep_person_id === null ? null : personId,
                }))}
                disabled={updateProject.isPending}
                placeholder="Search people…"
                allowCreate={can('organization.write')}
              />
            </div>
            <label className="pd-edit-field">
              <span>Priority</span>
              <select
                className="form-input"
                value={editForm.priority}
                onChange={(e) => setEditForm({ ...editForm, priority: Number(e.target.value) })}
                disabled={updateProject.isPending}
              >
                {[1, 2, 3, 4, 5].map((n) => (
                  <option key={n} value={n}>P{n}</option>
                ))}
              </select>
            </label>
          </div>
          <div className="pd-edit-row">
            <label className="pd-edit-field">
              <span>Court date</span>
              <DateInput
                value={editForm.court_date}
                onChange={(v) => setEditForm({ ...editForm, court_date: v })}
                disabled={updateProject.isPending}
              />
            </label>
            <label className="pd-edit-field">
              <span>Target end date</span>
              <DateInput
                value={editForm.target_end_date}
                onChange={(v) => setEditForm({ ...editForm, target_end_date: v })}
                disabled={updateProject.isPending}
              />
            </label>
          </div>
          {editError && <p className="pd-note-error" role="alert">{editError}</p>}
          <div className="pd-edit-actions">
            <IconAction
              action="cancel"
              onClick={() => setEditing(false)}
              disabled={updateProject.isPending}
            />
            <IconAction
              action="save"
              type="submit"
              disabled={updateProject.isPending || editForm.title.trim().length === 0}
              label={updateProject.isPending ? 'Saving…' : 'Save'}
            />
          </div>
        </form>
      )}

      {addClientOpen && (
        <AddClientModal
          open
          onClose={() => setAddClientOpen(false)}
          onCreated={(clientId, name) => {
            setNewClient({ id: clientId, name });
            setEditForm((f) => ({ ...f, client_id: clientId }));
            setAddClientOpen(false);
          }}
        />
      )}

      <section className="pd-meta">
        <div>
          <div className="pd-meta-label">Client</div>
          <div className="pd-meta-value">
            {p.client_company_name ?? p.client_name}
            {p.client_company_name && p.client_name && (
              <div className="pd-meta-sub">{p.client_name}</div>
            )}
          </div>
        </div>
        <div>
          <div className="pd-meta-label">Type</div>
          <div className="pd-meta-value">{caseTypeLabel(p.case_type)}</div>
        </div>
        <div>
          <div className="pd-meta-label">Billing</div>
          <div className="pd-meta-value">
            {billingLabel(p.billing_type)}
            {p.billing_type === 'flat_fee' && p.flat_fee_amount && (
              <div className="pd-meta-sub">{formatMoney(p.flat_fee_amount)}</div>
            )}
          </div>
        </div>
        <div>
          <div className="pd-meta-label">Priority</div>
          <div className="pd-meta-value">P{p.priority}</div>
        </div>
        {p.jurisdiction && (
          <div>
            <div className="pd-meta-label">Jurisdiction</div>
            <div className="pd-meta-value">{p.jurisdiction}</div>
          </div>
        )}
        {p.lead_attorney && (
          <div>
            <div className="pd-meta-label">Lead attorney</div>
            <div className="pd-meta-value">{p.lead_attorney}</div>
          </div>
        )}
        {p.court_date && (
          <div>
            <div className="pd-meta-label">Court date</div>
            <div className="pd-meta-value">{fmtDate(p.court_date)}</div>
          </div>
        )}
        {p.target_end_date && (
          <div>
            <div className="pd-meta-label">Target end</div>
            <div className="pd-meta-value">{fmtDate(p.target_end_date)}</div>
          </div>
        )}
      </section>

      {p.description && (
        <section className="pd-description">
          <h3>Description</h3>
          <p>{p.description}</p>
        </section>
      )}

      <section className="pd-block">
        <header className="pd-block-header">
          <h3>Invoicing</h3>
          <span className="pd-block-meta">
            {p.rep_inherited
              ? 'Using the client’s default representative'
              : 'This matter has its own representative'}
          </span>
        </header>
        <div className="pd-rep">
          <div className="pd-rep-pickers">
            <label className="pd-rep-field">
              <span>Represented by (law firm)</span>
              <OrgPicker
                value={p.rep_organization_id ?? null}
                onChange={(orgId) => void saveRep({ rep_organization_id: orgId })}
                disabled={!can('project.write') || updateProject.isPending}
              />
            </label>
            <label className="pd-rep-field">
              <span>Attorney</span>
              <PersonPicker
                value={p.rep_person_id ?? null}
                onChange={(personId, person) => void saveRep({
                  rep_person_id: personId,
                  // Keep the display-name snapshot in step. `lead_attorney`
                  // predates the FK and still feeds the header and the
                  // notification strings; letting the two disagree would show
                  // one attorney and notify about another.
                  lead_attorney: person?.displayName ?? null,
                })}
                disabled={!can('project.write') || updateProject.isPending}
                placeholder="Search people…"
                allowCreate={can('organization.write')}
              />
            </label>
          </div>
          {p.rep_inherited && (p.rep_organization_name || p.rep_person_name) && (
            <p className="pd-rep-hint">
              Inherited from the client:{' '}
              {[p.rep_organization_name, p.rep_person_name].filter(Boolean).join(' — ')}.
              Pick here to use someone else on this matter only.
            </p>
          )}

          <BillToBlock lines={p.bill_to} />

          {can('project.write') && (
            <label className="pd-rep-field">
              <span>Override (optional)</span>
              <textarea
                className="form-input pd-rep-override"
                value={override}
                onChange={(e) => setOverride(e.target.value)}
                onBlur={() => { if (override !== (p.bill_to_override ?? '')) void saveRep({ bill_to_override: override.trim() || null }); }}
                rows={4}
                maxLength={2000}
                placeholder="Replace the whole block — a billing department, a third-party payer. Leave empty to use the composed one."
                disabled={updateProject.isPending}
              />
            </label>
          )}
          {repError && <p className="pd-note-error" role="alert">{repError}</p>}
        </div>
      </section>

      <section className="pd-block">
        <header className="pd-block-header">
          <h3>Notes</h3>
          <span className="pd-block-meta">{notes.length} notes</span>
        </header>

        {can('project.write') && (
          <form className="pd-note-form" onSubmit={onAddNote}>
            <textarea
              className="form-input pd-note-input"
              value={noteText}
              onChange={(e) => setNoteText(e.target.value)}
              rows={2}
              placeholder="Add a quick note about this project…"
              maxLength={10_000}
              disabled={createNote.isPending}
            />
            <div className="pd-note-actions">
              <Toggle
                checked={noteImportant}
                onChange={setNoteImportant}
                label="Pin as important"
                disabled={createNote.isPending}
                className="pd-note-important"
              />
              <button
                type="submit"
                className="btn-cyan-primary pd-note-submit"
                disabled={createNote.isPending || noteText.trim().length === 0}
              >
                {createNote.isPending ? 'Saving…' : 'Add note'}
              </button>
            </div>
            {noteError && <p className="pd-note-error" role="alert">{noteError}</p>}
          </form>
        )}

        {notesQuery.isPending ? (
          <p className="pd-block-empty">Loading…</p>
        ) : notes.length === 0 ? (
          <p className="pd-block-empty">No notes on this project yet.</p>
        ) : (
          <ul className="pd-note-list">
            {notes.map((n) => (
              <li
                key={n.id}
                className={`pd-note ${n.is_important ? 'pd-note-important-row' : ''}`}
              >
                {n.is_important && (
                  <span className="pd-note-pin" aria-label="Pinned">★</span>
                )}
                <div className="pd-note-body">
                  <p className="pd-note-content">{n.content}</p>
                  <div className="pd-note-meta">
                    {n.created_by_name ?? `User #${n.created_by ?? '?'}`}
                    {' · '}
                    {new Date(n.created_at).toLocaleString(undefined, {
                      year: 'numeric',
                      month: 'short',
                      day: 'numeric',
                      hour: '2-digit',
                      minute: '2-digit',
                    })}
                  </div>
                </div>
                {can('project.write') && (
                  <button
                    type="button"
                    className="pd-note-delete"
                    onClick={() => {
                      if (confirm('Delete this note?')) {
                        deleteNote.mutate(n.id);
                      }
                    }}
                    disabled={deleteNote.isPending}
                    aria-label="Delete note"
                    title="Delete note"
                  >
                    ×
                  </button>
                )}
              </li>
            ))}
          </ul>
        )}
      </section>

      <ProjectMembersSection projectId={p.id} />

      <ProjectDocuments
        projectId={p.id}
        projectCaseNumber={p.case_number}
      />

      <section className="pd-block">
        <header className="pd-block-header">
          <h3>Tasks</h3>
          <span className="pd-block-meta">
            {tasksOpen} open · {tasksDone} done
          </span>
          {can('project.write') && !showApplyPicker && (
            <button
              type="button"
              className="btn-charcoal-secondary pd-apply-btn"
              onClick={() => setShowApplyPicker(true)}
            >
              Apply template
            </button>
          )}
        </header>

        {showApplyPicker && (
          <div className="pd-apply-bar">
            <select
              className="form-input pd-apply-select"
              value={applyTemplateId}
              onChange={(e) => {
                const v = e.target.value;
                setApplyTemplateId(v === '' ? '' : Number(v));
              }}
              disabled={applyTemplate.isPending}
              autoFocus
            >
              <option value="">Choose a template…</option>
              {(templatesQuery.data?.task_templates ?? []).map((t) => (
                <option key={t.id} value={t.id}>
                  {t.name}{t.case_type ? ` (${t.case_type.replace(/_/g, ' ')})` : ''} — {t.item_count ?? 0} items
                </option>
              ))}
            </select>
            <button
              type="button"
              className="btn-cyan-primary pd-apply-confirm"
              onClick={onApplyTemplate}
              disabled={applyTemplate.isPending || applyTemplateId === ''}
            >
              {applyTemplate.isPending ? 'Applying…' : 'Apply'}
            </button>
            <button
              type="button"
              className="btn-charcoal-secondary pd-apply-cancel"
              onClick={() => {
                setShowApplyPicker(false);
                setApplyError(null);
              }}
              disabled={applyTemplate.isPending}
            >
              Cancel
            </button>
          </div>
        )}
        {applyError && <p className="pd-note-error" role="alert">{applyError}</p>}

        {can('project.write') && (
          <form className="pd-task-form" onSubmit={onAddTask}>
            <input
              type="text"
              className="form-input pd-task-text"
              value={taskText}
              onChange={(e) => setTaskText(e.target.value)}
              placeholder="Add a task — e.g. Acquire suspect's MacBook"
              maxLength={500}
              disabled={createTask.isPending}
            />
            <select
              className="form-input pd-task-assignee"
              value={taskAssignee}
              onChange={(e) => {
                const v = e.target.value;
                setTaskAssignee(v === '' ? '' : Number(v));
              }}
              disabled={createTask.isPending}
            >
              <option value="">Unassigned</option>
              {assignableUsers.map((u) => (
                <option key={u.id} value={u.id}>{u.display_name}</option>
              ))}
            </select>
            <DateInput
              className="pd-task-due"
              value={taskDueDate}
              onChange={setTaskDueDate}
              disabled={createTask.isPending}
            />
            <button
              type="submit"
              className="btn-cyan-primary pd-task-submit"
              disabled={createTask.isPending || taskText.trim().length === 0}
            >
              {createTask.isPending ? 'Adding…' : 'Add'}
            </button>
          </form>
        )}
        {taskError && <p className="pd-note-error" role="alert">{taskError}</p>}

        {tasksQuery.isPending ? (
          <p className="pd-block-empty">Loading…</p>
        ) : tasks.length === 0 ? (
          <p className="pd-block-empty">No tasks on this project yet.</p>
        ) : (
          <ul className="pd-task-list">
            {tasks.map((t) => (
              <li
                key={t.id}
                className={`pd-task pd-task-${t.status}`}
              >
                <button
                  type="button"
                  className={`pd-task-status pd-task-status-${t.status}`}
                  onClick={() => onCycleTaskStatus(t.id, t.status)}
                  disabled={!can('project.write') || updateTask.isPending}
                  aria-label={`Status: ${t.status}. Click to advance.`}
                  title="Click to advance status"
                >
                  {t.status === 'done' ? '✓'
                   : t.status === 'in_progress' ? '◐'
                   : t.status === 'skipped' ? '−'
                   : '○'}
                </button>
                <div className="pd-task-body">
                  <p className="pd-task-text-display">{t.text}</p>
                  <div className="pd-task-meta">
                    {t.assignee_name ? (
                      <span className="pd-task-assignee-chip">{t.assignee_name}</span>
                    ) : (
                      <span className="pd-task-unassigned">Unassigned</span>
                    )}
                    {t.due_date && (
                      <span className="pd-task-due-chip">
                        Due {fmtDate(t.due_date)}
                      </span>
                    )}
                    <span className={`pd-task-status-label pd-task-status-${t.status}`}>
                      {t.status === 'in_progress' ? 'In progress' : t.status[0]!.toUpperCase() + t.status.slice(1)}
                    </span>
                  </div>
                </div>
                {can('project.write') && (
                  <div className="pd-task-actions">
                    {t.status !== 'skipped' && t.status !== 'done' && (
                      <button
                        type="button"
                        className="pd-task-skip"
                        onClick={() => updateTask.mutate({ id: t.id, status: 'skipped' })}
                        disabled={updateTask.isPending}
                        title="Skip this task"
                      >
                        Skip
                      </button>
                    )}
                    <button
                      type="button"
                      className="pd-note-delete"
                      onClick={() => {
                        if (confirm('Delete this task?')) deleteTask.mutate(t.id);
                      }}
                      disabled={deleteTask.isPending}
                      aria-label="Delete task"
                      title="Delete task"
                    >
                      ×
                    </button>
                  </div>
                )}
              </li>
            ))}
          </ul>
        )}
      </section>

      <ProjectMattersSection projectId={p.id} />

      <section className="pd-block">
        <header className="pd-block-header">
          <h3>Time Entries</h3>
          <span className="pd-block-meta">
            {timeEntries.length} entries · {totalHours} hrs · {unbilledCount} unbilled
            {wipTotals && Number(wipTotals.unbilled_time_value) > 0 && (
              <> · {formatMoney(wipTotals.unbilled_time_value)} unbilled value</>
            )}
          </span>
        </header>

        {timeEntriesQuery.isPending ? (
          <p className="pd-block-empty">Loading…</p>
        ) : timeEntries.length === 0 ? (
          <p className="pd-block-empty">No time logged on this project yet.</p>
        ) : (
          <table className="pd-table">
            <thead>
              <tr>
                <th>Date</th>
                <th>User</th>
                <th>Service</th>
                <th>Description</th>
                <th className="pd-numeric">Hours</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {timeEntries.map((te) => (
                <tr key={te.id}>
                  <td>{fmtDateMaybeTime(te.date)}</td>
                  <td>{te.user_display_name ?? `User #${te.user_id}`}</td>
                  <td>
                    {te.service_rate_name ?? `Rate #${te.service_rate_id}`}
                    {te.matter_name && <div className="pd-sub">{te.matter_name}</div>}
                  </td>
                  <td className="pd-cell-desc">{te.description}</td>
                  <td className="pd-numeric">{te.hours === null ? '—' : fmtHours(te.hours)}</td>
                  <td>
                    {te.invoice_id === null ? (
                      <span className="pd-pill pd-pill-warn">Unbilled</span>
                    ) : (
                      <Link to={`/invoices/${te.invoice_id}`} className="pd-pill pd-pill-ok">
                        Billed
                      </Link>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>

      <section className="pd-block">
        <header className="pd-block-header">
          <h3>Evidence</h3>
          <span className="pd-block-meta">{evidence.length} items</span>
        </header>
        {evidenceQuery.isPending ? (
          <p className="pd-block-empty">Loading…</p>
        ) : evidence.length === 0 ? (
          <p className="pd-block-empty">No evidence intake recorded.</p>
        ) : (
          <table className="pd-table">
            <thead>
              <tr>
                <th>Chain #</th>
                <th>Type</th>
                <th>Description</th>
                <th>Acquired</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {evidence.map((ev) => (
                <tr key={ev.id}>
                  <td>
                    <Link to={`/evidence/${ev.id}`} className="pd-link">
                      <code>{ev.chain_of_custody_number}</code>
                    </Link>
                  </td>
                  <td>{ev.evidence_type.replace(/_/g, ' ')}</td>
                  <td className="pd-cell-desc">{ev.description}</td>
                  <td>{fmtDate(ev.acquired_date)}</td>
                  <td>
                    <span className={`status-pill status-${ev.evidence_status}`}>
                      {evidenceStatusLabel(ev.evidence_status)}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>

      <section className="pd-block">
        <header className="pd-block-header">
          <h3>Expenses</h3>
          <span className="pd-block-meta">
            {expenses.length} items · {formatMoney(expensesTotal.toFixed(2))}
            {expensesReimbursable > 0 && (
              <> · {formatMoney(expensesReimbursable.toFixed(2))} reimbursable</>
            )}
          </span>
        </header>
        {expensesQuery.isPending ? (
          <p className="pd-block-empty">Loading…</p>
        ) : expenses.length === 0 ? (
          <p className="pd-block-empty">No expenses recorded.</p>
        ) : (
          <table className="pd-table">
            <thead>
              <tr>
                <th>Date</th>
                <th>Type</th>
                <th>Vendor</th>
                <th className="pd-numeric">Amount</th>
                <th>Reimbursable</th>
              </tr>
            </thead>
            <tbody>
              {expenses.map((ex) => (
                <tr key={ex.id}>
                  <td>{fmtDate(ex.date)}</td>
                  <td>
                    {ex.type}
                    {ex.matter_name && <div className="pd-sub">{ex.matter_name}</div>}
                  </td>
                  <td>{ex.vendor ?? '—'}</td>
                  <td className="pd-numeric">{formatMoney(ex.amount)}</td>
                  <td>
                    {ex.reimbursable ? (
                      <span className="pd-pill pd-pill-warn">Reimbursable</span>
                    ) : (
                      '—'
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>

      <section className="pd-block">
        <header className="pd-block-header">
          <h3>Invoices</h3>
          <span className="pd-block-meta">{invoices.length} invoices</span>
          {can('invoice.write') && (
            <Link to={`/invoices/new?project_id=${p.id}`} className="btn-cyan-primary pd-edit-btn">
              New invoice
            </Link>
          )}
        </header>
        {invoicesQuery.isPending ? (
          <p className="pd-block-empty">Loading…</p>
        ) : invoices.length === 0 ? (
          <p className="pd-block-empty">No invoices for this project yet.</p>
        ) : (
          <table className="pd-table">
            <thead>
              <tr>
                <th>Invoice #</th>
                <th>Issued</th>
                <th>Due</th>
                <th className="pd-numeric">Total</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {invoices.map((inv) => (
                <tr key={inv.id}>
                  <td>
                    <Link to={`/invoices/${inv.id}`} className="pd-link">
                      <code>{inv.invoice_number}</code>
                    </Link>
                    {inv.matter_name && <div className="pd-sub">{inv.matter_name}</div>}
                  </td>
                  <td>{fmtDate(inv.issue_date)}</td>
                  <td>{fmtDate(inv.due_date)}</td>
                  <td className="pd-numeric">{formatMoney(inv.total)}</td>
                  <td>
                    <span className={`iv-pill iv-status-${inv.status}`}>
                      {INVOICE_STATUS_LABEL[inv.status] ?? inv.status}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>
    </div>
  );
}
